// @flow
import React, { useCallback, useMemo } from "react";
import { connect, useSelector } from "react-redux";

import { Translate, useI18n } from "@quantum/i18n";

import { compose, flatten, prop, sortBy } from "ramda";

import {
  Tooltip,
  TooltipArrow,
  TooltipReference,
  useTooltipState,
} from "reakit/Tooltip";

import SelectInput, { useSelectState } from "../../comum/SelectInput";
import { bindModalTrigger } from "../../comum/ModalState";
import { ScrollTarget } from "../../comum";

import {
  type ActionCreator,
  type State,
  i18n,
  selecionarMedida,
  selecionarValorComboMedidas,
  getIdMedidaSelecionada,
  getIndiceMedidaEditada,
  getGrupamentoMedidaSelecionado,
  isAlterouGrupamentoMedidaSelecionado,
  getMedidasFiltradas,
  getValorComboMedidas,
  getModoApp,
} from "../../../state";
import {
  type MedidaTraduzida,
  type ChaveEnum,
  type Id,
  GRUPAMENTO_MEDIDAS_PERSONALIZADAS,
  GRUPAMENTO_MEDIDAS_SERIES,
  MODO_APP_NORMAL,
  MODO_APP_SERIES,
} from "../../../model";
import { GRUPAMENTO_MEDIDAS_QUALITATIVAS } from "../../../model";
import { normalizarString } from "../../../util";

import RelacionamentosMetodologiaModal from "../relacionamento-metodologia-modal";

import "./auto-complete-medidas.css";

type Prop = {
  desmarcarMedidasQuali: ActionCreator,
  grupamento: string,
  medidas: Array<MedidaTraduzida>,
  medidaSelecionada: Id | Array<Id>,
  modoExibicaoSelecionado: ChaveEnum,
  valorComboMedidas: string,
  onChange: (Id | Array<Id>) => mixed,
};

function AutoCompleteMedidas({
  desmarcarMedidasQuali,
  grupamento,
  medidas,
  medidaSelecionada,
  valorComboMedidas,
  onChange,
}: Prop) {
  const { translate } = useI18n();
  const tooltip = useTooltipState({ placement: "right" });
  const selectState = useSelectState("autoCompleteMedidas");
  const indiceMedidaEditada = useSelector(getIndiceMedidaEditada);
  const modoApp = useSelector(getModoApp);
  const jaAlterouGrupamento = useSelector(isAlterouGrupamentoMedidaSelecionado);

  const isMedidaQualitativa = grupamento === GRUPAMENTO_MEDIDAS_QUALITATIVAS;
  const isMedidaSeries =
    grupamento === GRUPAMENTO_MEDIDAS_SERIES && modoApp === MODO_APP_SERIES;
  const isMultiple = isMedidaQualitativa || isMedidaSeries;

  const focused =
    modoApp === MODO_APP_NORMAL ||
    (modoApp === MODO_APP_SERIES && (jaAlterouGrupamento || !isMultiple));

  const medidasSelecionadas: Array<Id> = useMemo(
    () => flatten([medidaSelecionada]).filter(Boolean),
    [medidaSelecionada]
  );

  const medidasOrdenadas: Array<MedidaTraduzida> = useMemo(
    () => sortBy(compose(normalizarString, prop("traducao")), medidas),
    [medidas]
  );

  const aoAlterarMedidasSelecionadas = useCallback(
    medidas => {
      if (isMedidaQualitativa || isMedidaSeries) {
        onChange(
          medidasOrdenadas
            .filter(medida => medidas.includes(medida.idConfiguracaoRelatorio))
            .map(prop("idConfiguracaoRelatorio"))
        );
      } else {
        onChange(medidas);
      }
    },
    [isMedidaQualitativa, isMedidaSeries, medidasOrdenadas, onChange]
  );

  if (
    modoApp === MODO_APP_SERIES &&
    grupamento === GRUPAMENTO_MEDIDAS_PERSONALIZADAS
  ) {
    return <></>;
  }

  return (
    <ScrollTarget name="edicaoDeMedida" className="AutoCompleteMedidas">
      <label className="AutoCompleteMedidas__label">
        <Translate>maiusculas.medidas</Translate>
        <span className="AutoCompleteMedidas__label--informacoes">
          {((isMedidaQualitativa && medidasSelecionadas.length >= 1) ||
            (isMedidaSeries && medidasSelecionadas.length >= 1)) && (
            <span
              className="AutoCompleteMedidas__label--desmarcar"
              onClick={desmarcarMedidasQuali}
            >
              <Translate>Comum.desmarcarTodos</Translate>
            </span>
          )}
          <>
            <RelacionamentosMetodologiaModal>
              {modal => (
                <span className="AutoCompleteMedidas__label--informacoes__metologia">
                  <TooltipReference {...tooltip}>
                    <Translate {...bindModalTrigger(modal)}>
                      menuFuncionalidades.metodologia
                    </Translate>
                  </TooltipReference>
                </span>
              )}
            </RelacionamentosMetodologiaModal>
            <Tooltip
              {...tooltip}
              className="AutoCompleteMedidas__label--informacoes__tooltip"
            >
              <Translate>configuracaoRelatorio.hintMetodologia</Translate>
              <TooltipArrow
                {...tooltip}
                className="AutoCompleteMedidas__label--informacoes__tooltip--arrow"
              />
            </Tooltip>
          </>
        </span>
      </label>
      <SelectInput
        {...selectState}
        focused={focused}
        key={indiceMedidaEditada}
        disabled={medidasOrdenadas.length === 0}
        className="AutoCompleteMedidas__select"
        value={
          isMedidaQualitativa || isMedidaSeries
            ? medidasSelecionadas
            : medidaSelecionada
        }
        valueToScroll={
          !medidaSelecionada || Array.isArray(medidaSelecionada)
            ? null
            : medidaSelecionada.toString()
        }
        options={medidasOrdenadas}
        campoId="idConfiguracaoRelatorio"
        campoDescricao="traducao"
        placeholder={translate("maiusculas.selecioneUmaMedida")}
        multiple={isMultiple}
        onChange={aoAlterarMedidasSelecionadas}
      />
    </ScrollTarget>
  );
}

export const criarFuncaoValidacaoSelecaoMedidas = (state: State) => () => {
  const medidaSelecionada: ?Id = getIdMedidaSelecionada(state);
  const { translate } = i18n(state);

  if (medidaSelecionada === null) {
    return { medida: translate("configuracaoRelatorio.selecioneUmaMedida") };
  }
  return null;
};

export default compose(
  connect(
    (state: State) => ({
      grupamento: getGrupamentoMedidaSelecionado(state),
      medidas: getMedidasFiltradas(state),
      medidaSelecionada: getIdMedidaSelecionada(state),
      valorComboMedidas: getValorComboMedidas(state),
    }),
    { selecionarMedida, selecionarValorComboMedidas },
    (state, { selecionarMedida, selecionarValorComboMedidas }) => ({
      ...state,
      desmarcarMedidasQuali: () => selecionarMedida([]),
      onChange: medida => {
        selecionarMedida(medida);
        selecionarValorComboMedidas(medida);
      },
    })
  )
)(AutoCompleteMedidas);
